import React from 'react';
import EmailComposer from './EmailComposer.jsx';
import { TimerCountdown } from './TimerCountdown.jsx';
import { ActivityItem } from './ActivityItem.jsx';
import { Button, EmptyState, Icon } from './ds';
import { getTeamTemplates } from '../api/outreach/templates.js';
import { useSyncReplyThread } from '../hooks/useSyncReplyThread';
import { sanitizeHtml } from '../utils/emailBody';

const isEditorEmpty = (html) => !html || html.replace(/<[^>]*>/g, '').trim() === '';

const pluralRu = (n, forms) => {
    const abs = Math.abs(n);
    const mod10 = abs % 10;
    const mod100 = abs % 100;
    if (mod10 === 1 && mod100 !== 11) return forms[0];
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
    return forms[2];
};

export function LeadDetailsEmailTab({
    leadId,
    panelTab,
    msgChannel,
    setMsgChannel,
    chanAddress,
    resolveMessageChannel,
    shownMessages,
    chatScrollRef,
    timer,
    onTimerExpire,
    setReplyToMessage,
    hasEmailChannel,
    canSendEmail = false,
    canManualEmailReply,
    handleEmailSendSubmit,
    templatesLoaded,
    setTemplatesLoaded,
    myTemplates,
    setMyTemplates,
    showTemplatePicker,
    setShowTemplatePicker,
    setEmailHtmlOverride,
    setEmailBodyDraft,
    emailAttachment,
    setEmailAttachment,
    setSelectedTemplateId,
    replyToMessage,
    replyEmailSubject,
    emailHtmlOverride,
    emailComposerMode,
    setEmailComposerMode,
    emailSending,
    emailBodyDraft,
    attachUploading,
    enrolledCampaigns,
    formatCampaignProgress,
    hasSentInCampaign,
    stageOneInfo,
    emailComposerError,
    emailSendError,
    stageOneCampaignError,
}) {
    const sync = useSyncReplyThread();
    return (
        <section className='details-modal__section details-modal__chat oc-lead-card__email' style={{ display: panelTab === 'email' ? 'flex' : 'none' }}>
            <header className='oc-lead-card__email-head'>
                <h3 className='oc-lead-card__email-title'>Переписка</h3>
                <div className='oc-lead-card__chan-switch' role='group' aria-label='Канал переписки'>
                    <button
                        type='button'
                        className={`oc-lead-card__chan-btn${msgChannel === 'email' ? ' oc-lead-card__chan-btn--active' : ''}`}
                        aria-pressed={msgChannel === 'email'}
                        onClick={() => setMsgChannel?.('email')}
                    >
                        <Icon name='envelope-simple' size='sm' /> Email
                    </button>
                    <button
                        type='button'
                        className={`oc-lead-card__chan-btn${msgChannel === 'ig' ? ' oc-lead-card__chan-btn--active' : ''}`}
                        aria-pressed={msgChannel === 'ig'}
                        onClick={() => setMsgChannel?.('ig')}
                    >
                        <Icon name='instagram-logo' size='sm' /> Instagram
                    </button>
                </div>
                <Button
                    hierarchy='tertiary'
                    size='small'
                    iconOnly
                    className='oc-lead-card__email-sync'
                    onClick={() => sync.mutate(leadId)} disabled={sync.isPending}
                    title='Обновить тред из Reply.io' aria-label='Обновить тред'
                >
                    <Icon name='arrows-clockwise' size='sm' />
                </Button>
            </header>
            <div className='oc-lead-card__chan-line'>
                <Icon name={msgChannel === 'ig' ? 'instagram-logo' : 'envelope-simple'} size='sm' />
                <span className='oc-lead-card__chan-addr'>{chanAddress || '—'}</span>
                <span>·</span>
                <span className='oc-lead-card__email-count'>{shownMessages.length} {pluralRu(shownMessages.length, ['сообщение', 'сообщения', 'сообщений'])}</span>
            </div>
            <div className='oc-lead-card__email-body'>
                <div ref={chatScrollRef} className='oc-lead-card__email-scroll'>
                    <TimerCountdown timer={timer} onExpire={onTimerExpire} />
                    {shownMessages.length === 0 && (
                        <EmptyState>
                            <span className='oc-lead-card__empty-title'>Нет сообщений</span>
                            <span className='oc-lead-card__empty-desc'>Сообщения для выбранного канала пока отсутствуют</span>
                        </EmptyState>
                    )}
                    {shownMessages.map(message => {
                        const ch = resolveMessageChannel?.(message) === 'instagram' ? 'ig' : 'email';
                        return (
                            <div key={message.id} className='oc-lead-card__msg-row'>
                                <span className={`oc-lead-card__chan-badge oc-lead-card__chan-badge--${ch}`}>
                                    {ch === 'ig' ? 'Instagram' : 'Email'}
                                </span>
                                <ActivityItem
                                    message={message}
                                    onReply={canSendEmail && panelTab === 'email' ? (msg) => {
                                        setReplyToMessage(msg);
                                    } : undefined}
                                />
                            </div>
                        );
                    })}
                </div>
                {canSendEmail && hasEmailChannel && msgChannel !== 'ig' && (
                            <form className='details-modal__composer' onSubmit={canManualEmailReply ? handleEmailSendSubmit : (event) => event.preventDefault()} noValidate>
                                {canManualEmailReply && (
                                    <>
                                        <div className='template-picker'>
                                            <button
                                                type='button'
                                                className='template-picker__toggle'
                                                onClick={async () => {
                                                    if (!templatesLoaded) {
                                                        const list = await getTeamTemplates().catch(() => []);
                                                        setMyTemplates(list);
                                                        setTemplatesLoaded(true);
                                                    }
                                                    setShowTemplatePicker(v => !v);
                                                }}
                                            >
                                                Шаблон
                                            </button>
                                            {showTemplatePicker && (
                                                <div className='template-picker__dropdown'>
                                                    {myTemplates.length === 0 ? (
                                                        <div className='template-picker__empty'>Нет шаблонов</div>
                                                    ) : myTemplates.map(t => (
                                                        <button
                                                            key={t.id}
                                                            type='button'
                                                            className='template-picker__item'
                                                            onClick={() => {
                                                                const body = t.body || '';
                                                                const isComplex = /<(table|div\s+style|style|tr|td|th)\b/i.test(body);
                                                                if (isComplex) {
                                                                    setEmailHtmlOverride(body);
                                                                    setEmailBodyDraft('');
                                                                } else {
                                                                    setEmailHtmlOverride(null);
                                                                    setEmailBodyDraft(body);
                                                                }
                                                                setSelectedTemplateId(t.id);
                                                                setShowTemplatePicker(false);
                                                            }}
                                                        >
                                                            <span className='template-picker__item-name'>{t.name}</span>
                                                            {t.body && (() => {
                                                                const text = t.body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                                                                return text && <span className='template-picker__item-preview'>{text.slice(0, 40)}{text.length > 40 ? '…' : ''}</span>;
                                                            })()}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {emailAttachment && (
                                            <div className='attachment-preview'>
                                                <span className='attachment-preview__name'>📄 {emailAttachment.originalName}</span>
                                                <button
                                                    type='button'
                                                    className='attachment-preview__remove'
                                                    onClick={() => setEmailAttachment(null)}
                                                    title='Удалить'
                                                >×</button>
                                            </div>
                                        )}
                                        {replyToMessage && (
                                            <div className='composer-reply-context'>
                                                <span className='composer-reply-context__icon'>↩</span>
                                                <span className='composer-reply-context__text'>
                                                    {replyToMessage.author || 'Лид'}: {(replyToMessage.bodyText || '').slice(0, 60)}{(replyToMessage.bodyText || '').length > 60 ? '…' : ''}
                                                </span>
                                                <button
                                                    type='button'
                                                    className='composer-reply-context__close'
                                                    onClick={() => setReplyToMessage(null)}
                                                    title='Убрать ответ'
                                                >×</button>
                                            </div>
                                        )}
                                        {replyEmailSubject && (
                                            <div className='composer-reply-subject'>
                                                <span className='composer-reply-subject__label'>Тема:</span>
                                                <span className='composer-reply-subject__value'>{replyEmailSubject}</span>
                                            </div>
                                        )}
                                        <div className='template-editor-mode-toggle'>
                                            <button
                                                type='button'
                                                className={`template-mode-btn ${emailComposerMode === 'visual' ? 'active' : ''}`}
                                                onClick={() => { setEmailComposerMode('visual'); setEmailHtmlOverride(null); }}
                                            >Визуальный</button>
                                            <button
                                                type='button'
                                                className={`template-mode-btn ${emailComposerMode === 'html' ? 'active' : ''}`}
                                                onClick={() => { setEmailComposerMode('html'); setEmailBodyDraft(''); }}
                                            >HTML</button>
                                        </div>
                                        {emailComposerMode === 'html' ? (
                                            <div className='details-modal__composer-input'>
                                                <textarea
                                                    className='form-input template-html-textarea'
                                                    // eslint-disable-next-line no-restricted-syntax
                                                    style={{ padding: '8px 12px', fontSize: '13px', background: 'var(--neutral-0)', border: '1px solid var(--neutral-40)', borderRadius: '6px', fontFamily: 'monospace', width: '100%' }}
                                                    rows={8}
                                                    placeholder='<table>…</table>'
                                                    value={emailHtmlOverride || ''}
                                                    onChange={(e) => setEmailHtmlOverride(e.target.value)}
                                                    disabled={emailSending}
                                                />
                                                {emailHtmlOverride && (
                                                    <div className='html-template-preview'>
                                                        <div className='html-template-preview__header'>
                                                            <span className='html-template-preview__badge'>HTML</span>
                                                            <span className='html-template-preview__hint'>Будет отправлен как есть</span>
                                                        </div>
                                                        <div className='html-template-preview__body' dangerouslySetInnerHTML={{ __html: sanitizeHtml(emailHtmlOverride) }} />
                                                        <div className='html-template-preview__actions'>
                                                            <button type='button' className='primary-button' onClick={handleEmailSendSubmit} disabled={emailSending || !emailHtmlOverride}>
                                                                {emailSending ? 'Отправка…' : '▶ Отправить'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : emailHtmlOverride ? (
                                            <div className='details-modal__composer-input'>
                                                <div className='html-template-preview'>
                                                    <div className='html-template-preview__header'>
                                                        <span className='html-template-preview__badge'>HTML шаблон</span>
                                                        <span className='html-template-preview__hint'>Будет отправлен как есть</span>
                                                        <button type='button' className='html-template-preview__close' onClick={() => { setEmailHtmlOverride(null); setSelectedTemplateId(null); }} title='Убрать'>×</button>
                                                    </div>
                                                    <div className='html-template-preview__body' dangerouslySetInnerHTML={{ __html: sanitizeHtml(emailHtmlOverride) }} />
                                                    <div className='html-template-preview__actions'>
                                                        <button type='button' className='primary-button' onClick={handleEmailSendSubmit} disabled={emailSending}>
                                                            {emailSending ? 'Отправка…' : '▶ Отправить'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className='details-modal__composer-input'>
                                                <EmailComposer
                                                    value={emailBodyDraft}
                                                    onChange={setEmailBodyDraft}
                                                    disabled={emailSending}
                                                    placeholder='Написать письмо...'
                                                    onAttach={(f) => setEmailAttachment({ file: f, originalName: f.name, mimeType: f.type })}
                                                    onSend={handleEmailSendSubmit}
                                                    sendDisabled={emailSending || attachUploading || (isEditorEmpty(emailBodyDraft) && !emailAttachment)}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                                {!canManualEmailReply && (
                                    <div className='details-modal__composer-meta'>
                                        <span className='details-modal__composer-hint'>
                                            Добавьте контакт в рассылку слева, в блоке «Email кампании».
                                        </span>
                                    </div>
                                )}
                                {!canManualEmailReply && enrolledCampaigns.length > 0 && (
                                    <div className='details-modal__composer-meta'>
                                        <span className='details-modal__composer-hint'>
                                            Прогресс: {enrolledCampaigns
                                                .map(item => `${item.name}: ${formatCampaignProgress(item.recipientStatus)}`)
                                                .join(' · ')}
                                        </span>
                                    </div>
                                )}
                                {!canManualEmailReply && enrolledCampaigns.length > 0 && !hasSentInCampaign && (
                                    <div className='details-modal__composer-meta'>
                                        <span className='details-modal__composer-hint'>
                                            Ручной email станет доступен после первой отправки из кампании.
                                        </span>
                                    </div>
                                )}
                                {stageOneInfo && (
                                    <div className='details-modal__composer-meta'>
                                        <span className='details-modal__composer-success'>{stageOneInfo}</span>
                                    </div>
                                )}
                                {emailComposerError && (
                                    <div className='details-modal__composer-meta'>
                                        <span className='details-modal__composer-error'>{emailComposerError}</span>
                                    </div>
                                )}
                                {emailSendError && (
                                    <div className='details-modal__composer-meta'>
                                        <span className='details-modal__composer-error'>{emailSendError}</span>
                                    </div>
                                )}
                                {stageOneCampaignError && (
                                    <div className='details-modal__composer-meta'>
                                        <span className='details-modal__composer-error'>{stageOneCampaignError}</span>
                                    </div>
                                )}
                            </form>
                        )}
            </div>
        </section>
    );
}
